import type {
  GetMyWorkspaceInput,
  GetWorkspaceRecordInput,
  WorkspaceActionInput,
  WorkspaceAttachFileInput,
  WorkspaceItemContract,
  WorkspaceRecordDetail,
  WorkspaceRemoveAttachmentInput,
  WorkspaceResponse,
} from "@/api/workspaceApi";

type MockWorkspaceItem = WorkspaceItemContract & {
  description?: string | null;
  item_code?: string | null;
  price?: number | null;
  care_service_id?: string | null;
  case_choice?: "wellness" | "continue_case" | "new_case" | null;
  start_date?: string | null;
  end_date?: string | null;
  result?: string | null;
  report?: string | null;
  image?: string | null;
  clinical_store?: Record<string, any>;
  vital_history_store?: any[];
  notes_store?: string[];
  attachments_store?: any[];
};

// Clinical-note catalogue options (mirror get_visit_workbench's top-level arrays, in sort_order).
const MOCK_WS_ASSESSMENT_FINDING_OPTIONS = [
  { code: "dehydration_mild", label_en: "Mild dehydration", label_ar: "جفاف خفيف" },
  { code: "abdominal_pain", label_en: "Abdominal pain", label_ar: "ألم بطني" },
  { code: "fever", label_en: "Fever", label_ar: "حمى" },
  { code: "lethargy", label_en: "Lethargy", label_ar: "خمول" },
];
const MOCK_WS_CLIENT_OBSERVATION_OPTIONS = [
  { code: "vomiting", label_en: "Vomiting", label_ar: "قيء" },
  { code: "diarrhea", label_en: "Diarrhea", label_ar: "إسهال" },
  { code: "reduced_appetite", label_en: "Reduced appetite", label_ar: "قلة الشهية" },
];
const MOCK_WS_OWNER_INSTRUCTION_OPTIONS = [
  { code: "bland_diet", label_en: "Feed a bland diet", label_ar: "تقديم نظام غذائي خفيف" },
  { code: "rest", label_en: "Ensure plenty of rest", label_ar: "ضمان الراحة الكافية" },
  { code: "return_if_worse", label_en: "Return if symptoms worsen", label_ar: "العودة إذا ساءت الأعراض" },
];

const serviceItems: MockWorkspaceItem[] = [
  {
    id: "PCS-MOCK-001",
    name: "PCS-MOCK-001",
    source_type: "Service",
    source_doctype: "PetCareService",
    title: "Grooming - Bella",
    subtitle: "Assigned grooming service. Billing is automatic after completion.",
    status: "Pending",
    priority: "Normal",
    pet: { id: "PET-MOCK-001", name: "Bella", pet_name: "Bella", species: "Mammal", breed: "Pomeranian", weight: 8 },
    guardian: { id: "GUARD-MOCK-001", name: "Noor Ali", full_name: "Noor Ali", phone: "0770 111 2222", customer_id: "CUST-MOCK-001" },
    assignee: { id: "groomer@example.com", name: "Groomer Omar", user: "groomer@example.com" },
    due_at: "2026-05-11 11:30:00",
    scheduled_at: "2026-05-11 11:00:00",
    next_task: "Start grooming when Bella is ready.",
    badges: ["Grooming", "Auto billing"],
    actions: ["start_service", "finish_service", "close_service", "add_note"],
    visit_id: "VVT-MOCK-001",
    order_id: "VVT-MOCK-001-ORD-001",
    care_service_id: "CARE-GROOMING",
    item_code: null,
    price: null,
    description: "",
    creation: "2026-05-11 09:00:00",
    modified: "2026-05-11 09:20:00",
    notes_store: ["Coordinator requested grooming after doctor check."],
    attachments_store: [
      {
        id: "FILE-MOCK-BELLA-BEFORE",
        name: "bella-before.webp",
        file_name: "bella-before.webp",
        file_url: "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=480&q=80",
        type: "image/webp",
        size: 154000,
        creation: "2026-05-11 09:15:00",
      },
    ],
  },
  {
    id: "PCS-MOCK-002",
    name: "PCS-MOCK-002",
    source_type: "Service",
    source_doctype: "PetCareService",
    title: "Bathing - Rocky",
    subtitle: "Service is already underway.",
    status: "In Progress",
    priority: "Urgent",
    pet: { id: "PET-MOCK-002", name: "Rocky", pet_name: "Rocky", species: "Mammal", breed: "Golden Retriever", weight: 31 },
    guardian: { id: "GUARD-MOCK-002", name: "Omar Hassan", full_name: "Omar Hassan", phone: "0770 333 4444", customer_id: "CUST-MOCK-002" },
    assignee: { id: "nurse@example.com", name: "Nurse Noor", user: "nurse@example.com" },
    due_at: "2026-05-11 10:30:00",
    scheduled_at: "2026-05-11 10:00:00",
    overdue: true,
    next_task: "Finish bathing and add a short end comment.",
    badges: ["Bathing", "Overdue", "Auto billing"],
    actions: ["finish_service", "close_service", "add_note"],
    visit_id: "VVT-MOCK-002",
    order_id: "VVT-MOCK-002-ORD-002",
    care_service_id: "CARE-BATHING",
    item_code: null,
    price: null,
    start_date: "2026-05-11 10:05:00",
    description: "Pet is calm, medicated shampoo used.",
    creation: "2026-05-11 08:30:00",
    modified: "2026-05-11 10:08:00",
    notes_store: ["Use sensitive-skin shampoo."],
    attachments_store: [
      {
        id: "FILE-MOCK-ROCKY-SHAMPOO",
        name: "rocky-shampoo.webp",
        file_name: "rocky-shampoo.webp",
        file_url: "https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?auto=format&fit=crop&w=480&q=80",
        type: "image/webp",
        size: 188000,
        creation: "2026-05-11 10:15:00",
      },
    ],
  },
  {
    id: "PCS-MOCK-003",
    name: "PCS-MOCK-003",
    source_type: "Service",
    source_doctype: "PetCareService",
    title: "Dry Cleaning - Milo",
    subtitle: "Closed service kept for Pets Helped / Done testing.",
    status: "Completed",
    priority: "Normal",
    pet: { id: "PET-MOCK-003", name: "Milo", pet_name: "Milo", species: "Mammal", breed: "Persian Cat", weight: 4.5 },
    guardian: { id: "GUARD-MOCK-003", name: "Layla Kareem", full_name: "Layla Kareem", phone: "0770 555 6666", customer_id: "CUST-MOCK-003" },
    assignee: { id: "groomer@example.com", name: "Groomer Omar", user: "groomer@example.com" },
    due_at: "2026-05-10 13:00:00",
    scheduled_at: "2026-05-10 12:30:00",
    next_task: "Ready for supervisor review.",
    badges: ["Dry Cleaning", "Pet helped"],
    actions: ["add_note"],
    visit_id: "VVT-MOCK-003",
    order_id: "VVT-MOCK-003-ORD-003",
    care_service_id: "CARE-DRY-CLEANING",
    item_code: "تنظيف جاف",
    price: 10000,
    start_date: "2026-05-10 12:35:00",
    end_date: "2026-05-10 12:55:00",
    description: "Completed without issues.",
    creation: "2026-05-10 11:50:00",
    modified: "2026-05-10 12:55:00",
    notes_store: ["Owner picked up happy pet."],
    attachments_store: [],
  },
];

const diagnosticItems: MockWorkspaceItem[] = [
  {
    id: "LAB-MOCK-001",
    name: "LAB-MOCK-001",
    source_type: "Lab",
    source_doctype: "Lab",
    title: "CBC - Bella",
    subtitle: "Pending sample processing.",
    status: "Pending",
    priority: "Urgent",
    pet: { id: "PET-MOCK-001", name: "Bella", pet_name: "Bella", species: "Mammal", weight: 8 },
    guardian: { id: "GUARD-MOCK-001", name: "Noor Ali", full_name: "Noor Ali" },
    assignee: { id: "lab@example.com", name: "Lab Team", user: "lab@example.com" },
    due_at: "2026-05-11 12:00:00",
    next_task: "Start test and enter result.",
    badges: ["Lab"],
    actions: ["start_test", "save_result", "release", "add_note"],
    visit_id: "VVT-MOCK-001",
    order_id: "VVT-MOCK-001-ORD-004",
    creation: "2026-05-11 09:40:00",
    modified: "2026-05-11 09:45:00",
    notes_store: [],
    attachments_store: [],
  },
  {
    id: "RAD-MOCK-001",
    name: "RAD-MOCK-001",
    source_type: "Radiology",
    source_doctype: "Imaging",
    title: "Chest X-Ray - Rocky",
    subtitle: "Report is in progress.",
    status: "In Progress",
    priority: "Normal",
    pet: { id: "PET-MOCK-002", name: "Rocky", pet_name: "Rocky", species: "Mammal", weight: 31 },
    guardian: { id: "GUARD-MOCK-002", name: "Omar Hassan", full_name: "Omar Hassan" },
    assignee: { id: "radiology@example.com", name: "Radiology Team", user: "radiology@example.com" },
    due_at: "2026-05-11 15:00:00",
    next_task: "Save report and release.",
    badges: ["Radiology"],
    actions: ["save_result", "release", "add_note"],
    visit_id: "VVT-MOCK-002",
    order_id: "VVT-MOCK-002-ORD-005",
    report: "Mild bronchial pattern. No acute fracture.",
    creation: "2026-05-11 10:10:00",
    modified: "2026-05-11 10:50:00",
    notes_store: ["Image acquired, report draft started."],
    attachments_store: [],
  },
  {
    id: "LAB-MOCK-002",
    name: "LAB-MOCK-002",
    source_type: "Lab",
    source_doctype: "Lab",
    title: "Skin Scrape - Milo",
    subtitle: "Released diagnostic record.",
    status: "Completed",
    priority: "Normal",
    pet: { id: "PET-MOCK-003", name: "Milo", pet_name: "Milo", species: "Mammal", weight: 4.5 },
    guardian: { id: "GUARD-MOCK-003", name: "Layla Kareem", full_name: "Layla Kareem" },
    assignee: { id: "lab@example.com", name: "Lab Team", user: "lab@example.com" },
    due_at: "2026-05-10 14:30:00",
    next_task: "Released to doctor.",
    badges: ["Released"],
    actions: ["add_note"],
    visit_id: "VVT-MOCK-003",
    order_id: "VVT-MOCK-003-ORD-006",
    result: "No mites seen.",
    creation: "2026-05-10 13:30:00",
    modified: "2026-05-10 14:10:00",
    notes_store: ["Released result to visit."],
    attachments_store: [],
  },
];

const visitItems = new Map<string, MockWorkspaceItem>();

function clone<T>(value: T): T {
  return structuredClone(value);
}

function allItems() {
  return [...serviceItems, ...diagnosticItems, ...visitItems.values()];
}

function isVisitSource(sourceType: string) {
  const source = sourceType.trim().toLowerCase();

  return source === "visit" || source === "vet visit";
}

function matchesSource(item: MockWorkspaceItem, sourceType: string) {
  const source = sourceType.trim().toLowerCase();
  const itemSource = String(item.source_type ?? "").toLowerCase();
  const doctype = String(item.source_doctype ?? "").toLowerCase();

  if (!source)
    return true;
  if (itemSource === source || doctype === source)
    return true;
  if (source === "service")
    return doctype === "petcareservice" || itemSource.includes("service");
  if (source === "radiology")
    return itemSource.includes("radiology") || doctype === "imaging";
  if (source === "lab")
    return itemSource.includes("lab");
  if (source === "visit")
    return doctype === "vet visit" || itemSource === "visit";

  return false;
}

function sourceRows(mode: string) {
  if (mode === "doctor")
    return [...visitItems.values()];
  if (mode === "service")
    return serviceItems;
  if (mode === "diagnostics")
    return diagnosticItems;
  if (mode === "all" || mode === "management")
    return allItems();

  return [];
}

function byStatus(rows: MockWorkspaceItem[]) {
  return rows.reduce<Record<string, number>>((result, row) => {
    const status = String(row.status ?? "Open");

    result[status] = (result[status] ?? 0) + 1;

    return result;
  }, {});
}

function bySource(rows: MockWorkspaceItem[]) {
  return rows.reduce<Record<string, number>>((result, row) => {
    const source = String(row.source_type ?? "Workspace");

    result[source] = (result[source] ?? 0) + 1;

    return result;
  }, {});
}

function queueItem(row: MockWorkspaceItem): WorkspaceItemContract {
  const { notes_store, attachments_store, description, item_code, price, start_date, end_date, result, report, image, ...item } = row;

  return clone(item);
}

function visitCaseContext(row: MockWorkspaceItem) {
  const choice = row.case_choice ?? null;
  const activeEpisode = choice && choice !== "wellness"
    ? {
        name: `${row.name}-EPISODE`,
        title: `${(row.pet as any)?.name || (row.pet as any)?.pet_name || "Pet"} active case`,
        status: "Under Treatment",
        priority: row.priority || "Normal",
        diagnosis: "Mock active case",
        started_on: row.creation || null,
        next_due_on: null,
        summary: "Mock case context generated by the workspace API.",
      }
    : null;

  return {
    doctor_case_choice: choice,
    case_choice_required: !choice,
    visit_care_episode: activeEpisode?.name ?? null,
    profile_active_episode: activeEpisode?.name ?? null,
    active_episode: activeEpisode,
    episode_status: activeEpisode?.status ?? null,
    can_continue_case: true,
    can_open_new_case: !activeEpisode,
  };
}

function detailFor(row: MockWorkspaceItem): WorkspaceRecordDetail {
  const isVisit = row.source_type === "Visit" || row.source_doctype === "Vet Visit";

  // Match the real get_record shape: a focused record's OWN attachments live under
  // focus_source.detail.attachments (every row) and, de-duplicated by file_url + carrying
  // is_image, under linked_records[].result_files — NOT the top-level `attachments`, which for
  // a procedure is the parent visit's. (The store here holds the focused row's own files.)
  const focusedAttachments = row.attachments_store ?? [];
  const seenFileUrls = new Set<string>();
  const focusedResultFiles = focusedAttachments
    .filter((att: any) => {
      const url = String(att?.file_url ?? "").trim().toLowerCase();
      if (!url || seenFileUrls.has(url))
        return false;
      seenFileUrls.add(url);

      return true;
    })
    .map((att: any) => {
      const ref = String(att?.file_name ?? att?.file_url ?? "").split("?")[0];
      const ext = ref.split(".").pop()?.toLowerCase() ?? "";

      return { ...att, is_image: ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic", "heif", "avif"].includes(ext) };
    });

  const serviceDetail = row.source_type === "Service"
    ? {
        name: row.name,
        source_type: row.source_type,
        source_doctype: row.source_doctype,
        title: row.title,
        status: row.status,
        provider: row.assignee,
        care_service_id: row.care_service_id,
        item_code: row.item_code,
        price: row.price,
        start_date: row.start_date,
        end_date: row.end_date,
        description: row.description,
        attachments: focusedAttachments,
      }
    : undefined;

  const diagnosticDetail = row.source_type !== "Service" && !isVisit
    ? {
        name: row.name,
        source_type: row.source_type,
        source_doctype: row.source_doctype,
        title: row.title,
        status: row.status,
        result: row.result,
        report: row.report,
        image: row.image,
        attachments: focusedAttachments,
      }
    : undefined;

  return clone({
    summary: {
      name: row.name,
      source_type: row.source_type,
      source_doctype: row.source_doctype,
      title: row.title,
      status: row.status ?? undefined,
      priority: row.priority ?? undefined,
      next_task: row.next_task ?? undefined,
      modified: row.modified ?? undefined,
      creation: row.creation ?? undefined,
    },
    pet: row.pet as any,
    guardian: row.guardian as any,
    assignee: row.assignee as any,
    case_context: isVisit ? visitCaseContext(row) : undefined,
    clinical: isVisit
      ? {
          ...(row.clinical_store ?? {}),
          chief_complaint: "Mock visit complaint",
          intake_summary: row.clinical_store?.intake_summary ?? row.subtitle,
          overview: row.clinical_store?.overview ?? row.description ?? "Mock visit loaded through workspace API.",
          examination: row.clinical_store?.examination ?? row.clinical_store?.examination_notes ?? "No acute distress in mock exam.",
          diagnosis: row.clinical_store?.diagnosis ?? "",
          plan: row.clinical_store?.plan ?? row.clinical_store?.treatment_plan ?? "",
          assessment_findings: row.clinical_store?.assessment_findings ?? [],
          assessment_note: row.clinical_store?.assessment_note ?? "",
          client_observations: row.clinical_store?.client_observations ?? [],
          doctor_note: row.clinical_store?.doctor_note ?? "",
          owner_instruction_items: row.clinical_store?.owner_instruction_items ?? [],
          owner_instruction_note: row.clinical_store?.owner_instruction_note ?? "",
          illness: row.clinical_store?.illness ?? "",
          vitals: {
            weight: row.clinical_store?.weight ?? (row.pet as any)?.weight ?? null,
            temperature: row.clinical_store?.temperature ?? null,
            heart_rate: row.clinical_store?.heart_rate ?? null,
            respiratory_rate: row.clinical_store?.respiratory_rate ?? null,
            spo2: row.clinical_store?.spo2 ?? null,
            pain_score: row.clinical_store?.pain_score ?? null,
            note: row.clinical_store?.vitals_note ?? row.clinical_store?.note ?? null,
          },
          vital_signs: row.vital_history_store ?? [],
        }
      : undefined,
    assessment_finding_options: isVisit ? MOCK_WS_ASSESSMENT_FINDING_OPTIONS : undefined,
    client_observation_options: isVisit ? MOCK_WS_CLIENT_OBSERVATION_OPTIONS : undefined,
    owner_instruction_options: isVisit ? MOCK_WS_OWNER_INSTRUCTION_OPTIONS : undefined,
    orders: [
      {
        order_id: row.order_id,
        kind: row.source_type === "Visit" ? "visit" : row.source_type === "Service" ? "service" : row.source_type === "Lab" ? "lab" : "radiology",
        title: row.title,
        status: row.status,
        priority: row.priority,
        linked_doctype: row.source_doctype,
        linked_name: row.name,
      },
    ],
    linked_records: [
      {
        source_type: row.source_type,
        source_doctype: row.source_doctype,
        name: row.name,
        status: row.status,
        title: row.title,
        provider: row.assignee,
        care_service_id: row.care_service_id,
        start_date: row.start_date,
        end_date: row.end_date,
        modified: row.modified,
        result_files: focusedResultFiles,
      },
    ],
    notes: row.notes_store ?? [],
    attachments: row.attachments_store ?? [],
    billing: {
      billing_status: row.item_code ? "Draft Invoice" : "Unbilled",
      total: row.price ?? 0,
      paid: 0,
      balance: row.price ?? 0,
      currency: "IQD",
      billed: 0,
      billable_items: row.item_code
        ? [{
            id: `${row.name}-billing`,
            item_name: row.item_code,
            item_code: row.item_code,
            item_type: "Service",
            qty: 1,
            rate: row.price ?? 0,
            amount: row.price ?? 0,
            status: "Billable",
            linked_service_id: row.name,
          }]
        : [],
    },
    timeline: [
      {
        title: "Loaded from mock workspace API",
        meta: row.modified,
        color: "primary",
        icon: "tabler-database",
        creation: row.modified,
      },
    ],
    raw: { doctype: row.source_doctype || String(row.source_type), name: row.name },
    focus_source: {
      name: row.name,
      source_type: row.source_type,
      source_doctype: row.source_doctype,
      title: row.title,
      status: row.status ?? undefined,
      priority: row.priority ?? undefined,
      detail: serviceDetail ?? diagnosticDetail,
    },
    sourceType: row.source_type,
    name: row.name,
    title: row.title,
    subtitle: row.subtitle,
    status: row.status,
    priority: row.priority,
  });
}

function makeMockVisitItem(name: string): MockWorkspaceItem {
  const existing = visitItems.get(name);
  if (existing)
    return existing;

  const visit: MockWorkspaceItem = {
    id: `Visit:${name}`,
    name,
    source_type: "Visit",
    source_doctype: "Vet Visit",
    title: `Mock Vet Visit - ${name}`,
    subtitle: "Generated mock visit for workspace action testing.",
    status: "In Progress",
    priority: "Normal",
    pet: { id: "PET-MOCK-VISIT", name: "Luna", pet_name: "Luna", species: "Cat", breed: "Domestic Shorthair", weight: 4.2 },
    guardian: { id: "GUARD-MOCK-VISIT", name: "Omar Hassan", full_name: "Omar Hassan", phone: "+96500000000", customer_id: "CUST-MOCK-VISIT" },
    assignee: { id: "HCP-MOCK-VISIT", name: "Dr. Mock", user: "doctor@example.com", specialization: "General Practice" },
    scheduled_at: "2026-05-15 09:45:00",
    next_task: "Complete case",
    badges: ["Visit", "Mock"],
    actions: ["start_consultation", "set_case_choice", "save_clinical_note", "save_diagnoses", "create_orders", "complete_case"],
    case_sheet_id: "VCS-MOCK-VISIT",
    visit_type: "Consultation",
    billing_status: "Unbilled",
    follow_up_status: "Not Needed",
    case_choice: null,
    creation: "2026-05-15 09:45:00",
    modified: "2026-05-15 10:10:00",
    notes_store: [],
    attachments_store: [],
  };

  visitItems.set(name, visit);

  return visit;
}

function findItem(sourceType: string, name: string) {
  return allItems().find(item =>
    item.name === name
    && matchesSource(item, sourceType),
  ) ?? (isVisitSource(sourceType) && name ? makeMockVisitItem(name) : null);
}

function stamp(row: MockWorkspaceItem) {
  row.modified = new Date().toISOString().slice(0, 19).replace("T", " ");
}

function petWeight(row: MockWorkspaceItem) {
  const weight = Number((row.pet as any)?.weight);

  return Number.isFinite(weight) ? weight : null;
}

function autoBillingForService(row: MockWorkspaceItem) {
  const weight = petWeight(row);

  if (row.care_service_id === "CARE-BATHING") {
    if (weight !== null && weight <= 35)
      return { itemCode: "تحميم كلاب 35", rate: 35000 };

    return null;
  }

  if (row.care_service_id === "CARE-GROOMING") {
    if (weight !== null && weight <= 25)
      return { itemCode: "حلاقة كلب 25", rate: 25000 };
    if (weight !== null && weight <= 35)
      return { itemCode: "حلاقة كلب 35", rate: 35000 };

    return { itemCode: "حلاقة كلب 45", rate: 45000 };
  }

  if (row.care_service_id === "CARE-DRY-CLEANING")
    return { itemCode: "تنظيف جاف", rate: 10000 };

  return null;
}

function applyAutomaticBilling(row: MockWorkspaceItem) {
  const billing = autoBillingForService(row);

  if (!billing)
    throw new Error("Billing setup is missing. Ask an administrator to review this service.");

  row.item_code = billing.itemCode;
  row.price = billing.rate;
  row.badges = [...new Set([...(row.badges ?? []), "Auto billed"])];
}

export function mockGetMyWorkspace(input: GetMyWorkspaceInput): WorkspaceResponse {
  const search = String(input.search ?? "").trim().toLowerCase();
  const priority = String(input.priority ?? "").trim().toLowerCase();
  const status = String(input.status ?? "").trim().toLowerCase();
  const cursor = Number(input.cursor ?? 0);
  const limit = Number(input.limit ?? 50);
  const rows = sourceRows(String(input.mode ?? "service"))
    .filter(row => !search || [
      row.name,
      row.title,
      row.subtitle,
      row.status,
      row.priority,
      (row.pet as any)?.name,
      (row.guardian as any)?.name,
      (row.assignee as any)?.name,
      ...(row.badges ?? []),
    ].some(value => String(value ?? "").toLowerCase().includes(search)))
    .filter(row => !priority || String(row.priority ?? "").toLowerCase() === priority)
    .filter(row => !status || String(row.status ?? "").toLowerCase() === status);
  const pageRows = rows.slice(cursor, cursor + limit);

  return {
    mode: String(input.mode ?? "service"),
    user: "mock.user@example.com",
    metrics: {
      total: rows.length,
      overdue: rows.filter(row => row.overdue).length,
      urgent: rows.filter(row => ["urgent", "emergency"].includes(String(row.priority ?? "").toLowerCase())).length,
      by_source: bySource(rows),
      by_status: byStatus(rows),
    },
    items: pageRows.map(queueItem),
    cursor,
    next_cursor: cursor + limit < rows.length ? cursor + limit : null,
    total: rows.length,
  };
}

export function mockGetWorkspaceRecord(input: GetWorkspaceRecordInput): WorkspaceRecordDetail {
  const row = findItem(String(input.source_type ?? ""), input.name);

  if (!row)
    throw new Error(`Mock workspace record not found: ${input.source_type} ${input.name}`);

  return detailFor(row);
}

export function mockPerformWorkspaceAction(input: WorkspaceActionInput): WorkspaceRecordDetail {
  const row = findItem(String(input.source_type ?? ""), input.name);

  if (!row)
    throw new Error(`Mock workspace action target not found: ${input.source_type} ${input.name}`);

  const payload = input.payload ?? {};
  const note = String(payload.note ?? payload.description ?? "").trim();

  if (input.action === "start_service" || input.action === "start_test") {
    row.status = "In Progress";
    row.start_date ||= new Date().toISOString().slice(0, 19).replace("T", " ");
  }

  if (input.action === "start_consultation")
    row.status = "In Progress";

  if (input.action === "set_case_choice") {
    const choice = String(payload.doctor_case_choice ?? "wellness");
    row.case_choice = choice === "continue_case" || choice === "new_case" ? choice : "wellness";
  }

  if (input.action === "save_clinical_note" || input.action === "save_vitals") {
    row.clinical_store = {
      ...(row.clinical_store ?? {}),
      ...payload,
    };

    if (input.action === "save_vitals") {
      const vitalRow = {
        ...payload,
        recorded_at: new Date().toISOString().slice(0, 19).replace("T", " "),
        visit: row.name,
      };

      row.vital_history_store = [...(row.vital_history_store ?? []), vitalRow];
    }
  }

  if (input.action === "finish_service") {
    row.status = "Finished";
    row.end_date ||= new Date().toISOString().slice(0, 19).replace("T", " ");
  }

  if (input.action === "close_service" || input.action === "release") {
    row.status = "Completed";
    row.end_date ||= new Date().toISOString().slice(0, 19).replace("T", " ");
    if (row.source_type === "Service")
      applyAutomaticBilling(row);
    row.badges = [...new Set([...(row.badges ?? []), row.source_type === "Service" ? "Pet helped" : "Released"])];
  }

  if (input.action === "save_result") {
    if (row.source_type === "Radiology") {
      row.report = String(payload.report ?? payload.result ?? note ?? row.report ?? "");
      row.image = String(payload.image ?? row.image ?? "");
    } else {
      row.result = String(payload.result ?? note ?? row.result ?? "");
    }
    row.status = row.status === "Pending" ? "In Progress" : row.status;
  }

  if (note)
    row.notes_store = [...(row.notes_store ?? []), note];

  stamp(row);

  return detailFor(row);
}

export function mockAddWorkspaceNote(input: { source_type: string; name: string; note: string }): WorkspaceRecordDetail {
  const row = findItem(String(input.source_type ?? ""), input.name);

  if (!row)
    throw new Error(`Mock workspace note target not found: ${input.source_type} ${input.name}`);

  row.notes_store = [...(row.notes_store ?? []), input.note];
  stamp(row);

  return detailFor(row);
}

export function mockAttachWorkspaceFile(input: WorkspaceAttachFileInput): WorkspaceRecordDetail {
  const row = findItem(String(input.source_type ?? ""), input.name);

  if (!row)
    throw new Error(`Mock workspace attachment target not found: ${input.source_type} ${input.name}`);

  // Match the real endpoint exactly: it accepts `file_url` OR base64 `filedata` (alias
  // `content`) and rejects anything else. A mock that also accepted `data_url` was more
  // permissive than production and hid the "file_url or filedata is required" bug — do NOT
  // reintroduce that fallback.
  const fileUrl = input.payload.file_url;
  const filedata = input.payload.filedata ?? input.payload.content;
  if (!fileUrl && !filedata)
    throw new Error("file_url or filedata is required");

  row.attachments_store = [
    ...(row.attachments_store ?? []),
    {
      id: `${row.name}-file-${(row.attachments_store ?? []).length + 1}`,
      name: input.payload.file_name,
      file_name: input.payload.file_name,
      file_url: String(fileUrl || filedata),
      type: input.payload.content_type,
      size: input.payload.size,
      creation: new Date().toISOString(),
    },
  ];
  stamp(row);

  return detailFor(row);
}

export function mockRemoveWorkspaceAttachment(input: WorkspaceRemoveAttachmentInput): WorkspaceRecordDetail {
  const row = findItem(String(input.source_type ?? ""), input.name);

  if (!row)
    throw new Error(`Mock workspace attachment target not found: ${input.source_type} ${input.name}`);

  row.attachments_store = (row.attachments_store ?? []).filter((attachment) => {
    const attachmentId = String(attachment.id ?? attachment.name ?? attachment.file_name ?? "");

    return attachmentId !== input.file_id;
  });
  stamp(row);

  return detailFor(row);
}
